import React, { useCallback, useEffect, useState } from 'react';
import { BookOpen, Plus, Pencil, Trash2, ArrowUpToLine, Save, X } from 'lucide-react';
import IntestazioneSezione from '../components/comuni/IntestazioneSezione';
import EditorReferto from '../components/referti/EditorReferto';
import { testiAPI } from '../services/testiApi';
import { useAuthStore } from '../store/useStore';
import { useSporco, useUscitaProtetta } from '../utils/uscitaProtetta';
import '../styles/TestiRiutilizzabili.css';

/**
 * TestiRiutilizzabili — la libreria, in una schermata sola.
 *
 * Frasi da inserire in un referto e protocolli da consegnare al
 * paziente sono la stessa cosa con destinazioni diverse: un testo con
 * un titolo. Qui si vedono, si scrivono e - per l'amministrazione - si
 * promuovono da «mio» a «della struttura». Due portate, come le schede:
 * il medico compone dai condivisi e si fa i suoi; la promozione e' un
 * atto dell'amministrazione, non del medico.
 *
 * Chi vede cosa lo decide il server: un medico riceve i condivisi e i
 * suoi, l'amministrazione tutto, la segreteria i condivisi.
 */

const TIPI = [
    { chiave: 'frase', etichetta: 'Frase', spiegazione: 'da inserire in un referto o in una ricetta' },
    { chiave: 'protocollo', etichetta: 'Protocollo', spiegazione: 'da consegnare al paziente, per esempio dopo un trattamento' },
    { chiave: 'altro', etichetta: 'Altro', spiegazione: '' },
];
const etichettaTipo = (t) => TIPI.find((x) => x.chiave === t)?.etichetta || t;

const VUOTO = { titolo: '', tipo: 'frase', contenuto: { type: 'doc', content: [{ type: 'paragraph' }] } };

const messaggio = (e, ripiego) => e?.response?.data?.message || e?.message || ripiego;

/* Il modulo di scrittura: uno solo per creare e modificare. */
const ModuloTesto = ({ iniziale, onSalvato, onChiudi }) => {
    const [dati, setDati] = useState(iniziale || VUOTO);
    const [inCorso, setInCorso] = useState(false);
    const [errore, setErrore] = useState(null);
    const [versione, setVersione] = useState(0);
    const sporco = useSporco({ titolo: dati.titolo, tipo: dati.tipo, contenuto: { ...dati.contenuto, __id: undefined } }, versione);
    const chiedi = useUscitaProtetta(sporco);

    useEffect(() => { setDati(iniziale || VUOTO); setVersione((v) => v + 1); }, [iniziale]);

    const salva = async () => {
        setInCorso(true);
        setErrore(null);
        try {
            const { __id, ...contenuto } = dati.contenuto || {};
            const corpo = { titolo: dati.titolo, tipo: dati.tipo, contenuto };
            const r = dati.id ? await testiAPI.aggiorna(dati.id, corpo) : await testiAPI.crea(corpo);
            setVersione((v) => v + 1);
            onSalvato(r.data?.data?.testo, r.data?.message);
        } catch (e) {
            setErrore(messaggio(e, 'Errore nel salvataggio'));
        } finally {
            setInCorso(false);
        }
    };

    const chiudi = () => { if (chiedi()) onChiudi(); };

    return (
        <div className="tr-modulo">
            {errore && <p className="rm-messaggio rm-messaggio-errore">{errore}</p>}
            <div className="tr-modulo-riga">
                <label className="tr-campo tr-campo-titolo">
                    <span>Titolo</span>
                    <input value={dati.titolo} maxLength={160}
                           onChange={(e) => setDati({ ...dati, titolo: e.target.value })}
                           placeholder="Es. Indicazioni dopo il filler" />
                </label>
                <label className="tr-campo">
                    <span>Tipo</span>
                    <select value={dati.tipo} onChange={(e) => setDati({ ...dati, tipo: e.target.value })}>
                        {TIPI.map((t) => <option key={t.chiave} value={t.chiave}>{t.etichetta}</option>)}
                    </select>
                </label>
            </div>
            <p className="tr-spiega">{TIPI.find((t) => t.chiave === dati.tipo)?.spiegazione}</p>
            <EditorReferto
                contenuto={dati.contenuto}
                onCambia={(c) => setDati((d) => ({ ...d, contenuto: c }))}
            />
            <div className="tr-modulo-azioni">
                <button type="button" className="rm-btn" onClick={chiudi} disabled={inCorso}>
                    <X size={15} /> Annulla
                </button>
                <button type="button" className="rm-btn rm-btn-primario" onClick={salva}
                        disabled={inCorso || !sporco || !dati.titolo.trim()}>
                    <Save size={15} /> {inCorso ? 'Salvo…' : 'Salva'}
                </button>
            </div>
        </div>
    );
};

const TestiRiutilizzabili = () => {
    const utente = useAuthStore((s) => s.user);
    const amministra = utente?.ruolo === 'admin';
    const collegato = Boolean(utente?.medico_id);

    const [testi, setTesti] = useState([]);
    const [vista, setVista] = useState('tutti');
    const [aperto, setAperto] = useState(null);      // null | 'nuovo' | testo
    const [avviso, setAvviso] = useState(null);
    const [errore, setErrore] = useState(null);

    const carica = useCallback(async () => {
        try {
            const r = await testiAPI.elenco(amministra ? { tutti: 1 } : {});
            setTesti(r.data?.data?.testi || []);
            setErrore(null);
        } catch (e) {
            setErrore(messaggio(e, 'Non riesco a leggere la libreria'));
        }
    }, [amministra]);

    useEffect(() => { carica(); }, [carica]);

    const visibili = testi.filter((t) => vista === 'tutti' || t.tipo === vista);
    const struttura = visibili.filter((t) => t.portata === 'struttura');
    const miei = visibili.filter((t) => t.portata === 'mio' && Number(t.medico_id) === Number(utente?.medico_id));
    const altrui = visibili.filter((t) => t.portata === 'mio' && Number(t.medico_id) !== Number(utente?.medico_id));

    const puoScrivere = (t) => (t.portata === 'struttura'
        ? ['admin', 'segreteria'].includes(utente?.ruolo)
        : amministra || Number(t.medico_id) === Number(utente?.medico_id));

    const promuovi = async (t) => {
        if (!window.confirm(`Promuovere «${t.titolo}» a testo della struttura?\n\nLo vedranno e potranno usarlo tutti i medici; da quel momento lo cura l'amministrazione.`)) return;
        try {
            const r = await testiAPI.promuovi(t.id);
            setAvviso(r.data?.message);
            carica();
        } catch (e) { setErrore(messaggio(e, 'Errore nella promozione')); }
    };

    const elimina = async (t) => {
        if (!window.confirm(`Eliminare «${t.titolo}»?\n\nI referti in cui è già stato inserito non cambiano: il testo lì dentro è una copia.`)) return;
        try {
            await testiAPI.cancella(t.id);
            if (aperto?.id === t.id) setAperto(null);
            carica();
        } catch (e) { setErrore(messaggio(e, 'Errore nell\'eliminazione')); }
    };

    const Elenco = ({ titolo, voci, nota, promuovibili = false }) => (
        <section className="tr-gruppo">
            <h2>{titolo} <span className="tr-conta">{voci.length}</span></h2>
            {nota && <p className="tr-nota">{nota}</p>}
            {voci.length === 0 ? <p className="tr-vuoto">Nessun testo.</p> : (
                <ul className="tr-elenco">
                    {voci.map((t) => (
                        <li key={t.id} className={`tr-voce ${t.attivo === false ? 'tr-disattivo' : ''}`}>
                            <div className="tr-voce-testa">
                                <span className="tr-tipo">{etichettaTipo(t.tipo)}</span>
                                <strong>{t.titolo}</strong>
                                {t.medico_cognome && <span className="tr-autore">{t.medico_cognome} {t.medico_nome}</span>}
                                {t.attivo === false && <span className="tr-tipo">disattivato</span>}
                            </div>
                            <p className="tr-anteprima">{t.anteprima}{t.anteprima?.length >= 160 ? '…' : ''}</p>
                            <div className="tr-voce-azioni">
                                {puoScrivere(t) && (
                                    <button type="button" className="rm-btn" onClick={() => setAperto(t)}>
                                        <Pencil size={14} /> Modifica
                                    </button>
                                )}
                                {promuovibili && amministra && (
                                    <button type="button" className="rm-btn" onClick={() => promuovi(t)}
                                            title="Da personale a condiviso con tutti i medici">
                                        <ArrowUpToLine size={14} /> Promuovi
                                    </button>
                                )}
                                {puoScrivere(t) && (
                                    <button type="button" className="rm-btn rm-btn-pericolo" onClick={() => elimina(t)}>
                                        <Trash2 size={14} /> Elimina
                                    </button>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );

    const puoCreare = ['admin', 'segreteria', 'medico', 'medico_senior'].includes(utente?.ruolo);

    return (
        <div className="tr-pagina">
            <IntestazioneSezione
                titolo="Testi riutilizzabili"
                icona={<BookOpen size={22} aria-hidden="true" />}
                sottotitolo={collegato
                    ? 'Frasi e protocolli che scrivi una volta e inserisci nei referti. I tuoi li vedi solo tu; quelli della struttura li vedono tutti.'
                    : 'Frasi e protocolli della struttura, a disposizione di tutti i medici.'}
                viste={[{ chiave: 'tutti', etichetta: 'Tutti', contatore: testi.length },
                        ...TIPI.map((t) => ({ chiave: t.chiave, etichetta: t.etichetta + (t.chiave === 'altro' ? '' : ''),
                                              contatore: testi.filter((x) => x.tipo === t.chiave).length }))]}
                vista={vista}
                onVista={setVista}
                azione={puoCreare && (
                    <button type="button" className="rm-btn rm-btn-primario" onClick={() => setAperto('nuovo')}>
                        <Plus size={15} /> Nuovo testo
                    </button>
                )}
            />

            {errore && <p className="rm-messaggio rm-messaggio-errore">{errore}</p>}
            {avviso && <p className="rm-messaggio rm-messaggio-conferma">{avviso}
                <button type="button" className="tr-chiudi-avviso" onClick={() => setAvviso(null)}><X size={14} /></button></p>}

            {aperto && (
                <ModuloTesto
                    iniziale={aperto === 'nuovo' ? null : aperto}
                    onSalvato={(t, msg) => { setAperto(null); setAvviso(msg || 'Salvato.'); carica(); }}
                    onChiudi={() => setAperto(null)}
                />
            )}

            <Elenco titolo="Della struttura" voci={struttura}
                    nota={amministra ? 'Condivisi con tutti i medici. Li cura l\'amministrazione.' : null} />
            {collegato && (
                <Elenco titolo="I miei" voci={miei}
                        nota="Li vedi solo tu. Se vuoi che diventino della struttura, chiedi all'amministrazione di promuoverli." />
            )}
            {amministra && (
                <Elenco titolo="Personali dei medici" voci={altrui} promuovibili
                        nota="Scritti dai medici per sé. «Promuovi» li rende della struttura: da quel momento li vedono tutti e li cura l'amministrazione." />
            )}
        </div>
    );
};

export default TestiRiutilizzabili;
